import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchAllStatements,
  fetchStatementDetail,
  markStatementCompleted,
  setResumedStatementMeta,
  setParsedHeaders,
  setColumnMapping,
  setCurrentStep,
  StatementRecord,
} from "../../../redux/Commission/commissionStatementSlice";
import apiServices from "../../../service";

const T1 = "#0f172a";
const T2 = "#475569";
const T3 = "#94a3b8";
const GRN = "#2b8a3e";
const GRNL = "#e6f9f0";
const ORA = "#c05621";
const ORAL = "#fff8f0";
const BRD = "#e2e5ea";
const BRD2 = "#c8cdd5";
const NAVY = "#205393";
const SUR = "#f8f9fb";

type Tab = "inprogress" | "completed";

interface Props {
  open: boolean;
  onClose: () => void;
}

const fd = (iso: string) => {
  if (!iso) return "—";
  const d = iso.split("T")[0];
  const [y, m, day] = d.split("-");
  if (!y || !m || !day) return iso;
  return `${day}/${m}/${y}`;
};

const fdt = (iso: string) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  return dt.toLocaleDateString("en-GB") + " " + dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const PAGE_SIZES = [10, 20, 50];

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ResumeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const SessionsModal: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { allStatements, allStatementsTotal, loading, loadingDetail } = useSelector((s: RootState) => s.commissionStatement);
  const [tab, setTab] = useState<Tab>("inprogress");
  const [confirmCompleteId, setConfirmCompleteId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  useEffect(() => {
    if (open) setPage(1);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirmCompleteId) setConfirmCompleteId(null);
      else onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, confirmCompleteId, onClose]);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchAllStatements({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      status: tab === "inprogress" ? "IN_PROGRESS" : "COMPLETED",
    }));
  }, [open, page, pageSize, tab, dispatch]);

  const handleTabSwitch = (t: Tab) => {
    setTab(t);
    setPage(1);
  };

  const handleResume = useCallback((statement: StatementRecord) => {
    const cm = statement.ColumnMapping;
    const mappingReady = !!(cm?.caseRef && cm?.actual);
    const hasRows = (statement.TotalRows ?? 0) > 0;

    dispatch(setResumedStatementMeta({
      providerId: statement.ProviderId,
      providerName: statement.ProviderName,
      startDate: statement.StartDate,
      endDate: statement.EndDate,
      receiptDate: statement.ReceiptDate,
      fileName: statement.FileName,
      statementId: statement.CommissionStatementId,
      columnMapping: hasRows && mappingReady ? cm : null,
    }));

    if (hasRows && mappingReady) {
      // Column mapping complete and rows exist → go to Step 3 (Validate & Match)
      dispatch(fetchStatementDetail(statement.CommissionStatementId));
    } else {
      // Mapping incomplete or no rows → go to Step 2 (Column Mapping)
      dispatch(setParsedHeaders(statement.Headers ?? []));
      if (cm) dispatch(setColumnMapping(cm));
      dispatch(setCurrentStep(2));
    }
    onClose();
  }, [dispatch, onClose]);

  const handleDownloadReport = useCallback(async (statementId: string) => {
    setDownloadingId(statementId);
    setReportError(null);
    try {
      const res = await apiServices.commissionStatement.generateCommissionStatementReport(statementId);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `Receipting-Summary-${statementId.slice(0, 8)}-${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setReportError(e?.response?.data?.message || e?.message || "Failed to generate report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleMarkComplete = useCallback(async (statementId: string) => {
    setConfirmCompleteId(null);
    const result = await dispatch(markStatementCompleted({ statementId }));
    if (markStatementCompleted.fulfilled.match(result)) {
      setSuccessToast("Upload marked as completed successfully.");
      setTimeout(() => setSuccessToast(null), 4000);
      const listResult = await dispatch(fetchAllStatements({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        status: "IN_PROGRESS",
      }));
      // Marking the last "In Progress" row on a non-first page complete used
      // to leave the user stuck looking at that now-empty page -- step back
      // one page and refetch once more instead of settling on a blank page.
      if (
        fetchAllStatements.fulfilled.match(listResult) &&
        (listResult.payload?.data?.length ?? 0) === 0 &&
        page > 1
      ) {
        setPage(page - 1);
        dispatch(fetchAllStatements({
          limit: pageSize,
          offset: (page - 2) * pageSize,
          status: "IN_PROGRESS",
        }));
      }
    }
  }, [dispatch, pageSize, page]);

  if (!open) return null;

  const totalPages = Math.max(1, Math.ceil(allStatementsTotal / pageSize));
  const pageRows = allStatements;
  const isCompletedTab = tab === "completed";

  const thStyle: React.CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: SUR,
    borderBottom: `2px solid ${BRD}`,
    padding: "8px 10px",
    textAlign: "left",
    fontSize: "11.5px",
    fontWeight: 600,
    color: T2,
    whiteSpace: "nowrap",
  };

  const tdStyle: React.CSSProperties = {
    padding: "9px 10px",
    borderBottom: `1px solid ${BRD}`,
    fontSize: "12px",
    color: T1,
    verticalAlign: "middle",
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    background: "none",
    border: "none",
    borderBottom: active ? `2px solid ${NAVY}` : "2px solid transparent",
    padding: "8px 16px",
    fontSize: "12.5px",
    fontWeight: active ? 700 : 500,
    color: active ? NAVY : T3,
    cursor: "pointer",
    lineHeight: 1.4,
    marginBottom: "-1px",
  });

  const confirmTarget = confirmCompleteId
    ? allStatements.find((s) => s.CommissionStatementId === confirmCompleteId)
    : null;
  const confirmPending = confirmTarget
    ? (confirmTarget.TotalRows || 0) - (confirmTarget.ReceiptedRows || 0)
    : 0;

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div role="dialog" aria-modal="true" aria-label="Upload History" style={{ background: "#fff", borderRadius: "7px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", width: "920px", maxWidth: "97vw", display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `1px solid ${BRD}` }}>
            <div>
              <span style={{ fontSize: "14px", fontWeight: 700, color: T1 }}>Upload History</span>
              <span style={{ fontSize: "12px", color: T3, marginLeft: "10px" }}>All uploads</span>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T3, fontSize: "20px", cursor: "pointer", lineHeight: 1, padding: "0 2px" }} aria-label="Close">×</button>
          </div>

          {/* Success toast */}
          {successToast && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: GRNL, border: `1px solid ${GRN}`, padding: "8px 14px", fontSize: "12.5px", color: GRN }}>
              <span style={{ flex: 1 }}>{successToast}</span>
              <button onClick={() => setSuccessToast(null)} style={{ background: "none", border: "none", color: GRN, cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: "0 2px" }}>×</button>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${BRD}`, paddingLeft: "4px", background: "#fff" }}>
            <button style={tabBtnStyle(tab === "inprogress")} onClick={() => handleTabSwitch("inprogress")}>
              In Progress
            </button>
            <button style={tabBtnStyle(tab === "completed")} onClick={() => handleTabSwitch("completed")}>
              Completed
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {reportError && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#ffe6e6", border: "1px solid #c92a2a", borderRadius: "5px", margin: "10px 16px 0", padding: "8px 12px", fontSize: "12.5px", color: "#c92a2a" }}>
                <span style={{ flex: 1 }}>{reportError}</span>
                <button onClick={() => setReportError(null)} style={{ background: "none", border: "none", color: "#c92a2a", cursor: "pointer", fontSize: "16px", lineHeight: 1, padding: "0 2px" }}>×</button>
              </div>
            )}
            {loading.allStatements ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                <Spinner size="small" />
              </div>
            ) : allStatements.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: T3, fontSize: "13px" }}>
                {isCompletedTab ? "No completed uploads found." : "No in-progress uploads found."}
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Upload Date / Time</th>
                      <th style={thStyle}>File Name</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Records</th>
                      {!isCompletedTab && <th style={{ ...thStyle, textAlign: "right" }}>Pending</th>}
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: "center", width: isCompletedTab ? "120px" : "230px", minWidth: isCompletedTab ? "120px" : "230px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((s) => {
                      const pending = (s.TotalRows || 0) - (s.ReceiptedRows || 0);
                      const fileName = s.FileName || "Unknown file";
                      const isDownloading = downloadingId === s.CommissionStatementId;

                      return (
                        <tr key={s.CommissionStatementId} style={{ background: "#fff" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = SUR)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                        >
                          <td style={tdStyle}>
                            <span style={{ color: T2 }}>{fdt(s.CreatedAt)}</span>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: "220px" }}>
                            <div style={{ fontWeight: 500, color: T1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={fileName}>
                              {fileName}
                            </div>
                            <div style={{ fontSize: "11px", color: T3, marginTop: "2px" }}>
                              {s.ProviderName} &nbsp;·&nbsp; {fd(s.StartDate)} – {fd(s.EndDate)}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace" }}>
                            {s.TotalRows || 0}
                          </td>
                          {!isCompletedTab && (
                            <td style={{ ...tdStyle, textAlign: "right" }}>
                              {pending > 0 ? (
                                <span style={{ background: ORAL, color: ORA, padding: "2px 8px", borderRadius: "20px", fontWeight: 600, fontSize: "11.5px" }}>
                                  {pending}
                                </span>
                              ) : (
                                <span style={{ background: GRNL, color: GRN, padding: "2px 8px", borderRadius: "20px", fontWeight: 600, fontSize: "11.5px" }}>
                                  0
                                </span>
                              )}
                            </td>
                          )}
                          <td style={tdStyle}>
                            {isCompletedTab ? (
                              <span style={{ background: GRNL, color: GRN, padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600 }}>
                                Completed
                              </span>
                            ) : (
                              <span style={{ background: ORAL, color: ORA, padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600 }}>
                                In Progress
                              </span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center", width: isCompletedTab ? "120px" : "230px", minWidth: isCompletedTab ? "120px" : "230px", whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "nowrap" }}>
                              {!isCompletedTab && (
                                <button
                                  onClick={() => handleResume(s)}
                                  disabled={loadingDetail}
                                  style={{
                                    background: NAVY, color: "#fff", border: "none", borderRadius: "5px",
                                    fontSize: "11.5px", fontWeight: 600, padding: "0 10px", height: "28px",
                                    cursor: loadingDetail ? "not-allowed" : "pointer",
                                    display: "inline-flex", alignItems: "center", gap: "5px",
                                    opacity: loadingDetail ? 0.6 : 1,
                                  }}
                                >
                                  <ResumeIcon />
                                  Resume
                                </button>
                              )}
                              <button
                                onClick={() => handleDownloadReport(s.CommissionStatementId)}
                                disabled={isDownloading}
                                style={{
                                  background: "#fff", color: NAVY, border: `1px solid ${NAVY}`, borderRadius: "5px",
                                  fontSize: "11.5px", fontWeight: 600, padding: "0 10px", height: "28px",
                                  cursor: isDownloading ? "not-allowed" : "pointer",
                                  display: "inline-flex", alignItems: "center", gap: "5px",
                                  opacity: isDownloading ? 0.6 : 1,
                                }}
                              >
                                {isDownloading ? <Spinner size="tiny" /> : <DownloadIcon />}
                                Report
                              </button>
                              {!isCompletedTab && (
                                <button
                                  onClick={() => setConfirmCompleteId(s.CommissionStatementId)}
                                  disabled={loading.markComplete}
                                  style={{
                                    background: "#2e7d32", color: "#fff", border: "none", borderRadius: "5px",
                                    fontSize: "11.5px", fontWeight: 600, padding: "0 10px", height: "28px",
                                    cursor: loading.markComplete ? "not-allowed" : "pointer",
                                    opacity: loading.markComplete ? 0.5 : 1,
                                  }}
                                >
                                  Mark Complete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer: pagination + rows-per-page */}
          {allStatementsTotal > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 16px", borderTop: `1px solid ${BRD}`, background: SUR, flexWrap: "wrap" }}>
              <div style={{ flex: 1 }} />
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: T2 }}>
                <span>Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  style={{ border: `1px solid ${BRD2}`, borderRadius: "4px", fontSize: "12px", padding: "2px 4px", color: T1, background: "#fff" }}
                >
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <span style={{ fontSize: "12px", color: T2 }}>
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, allStatementsTotal)} of {allStatementsTotal}
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  style={{ background: "#fff", border: `1px solid ${BRD2}`, borderRadius: "4px", padding: "2px 8px", fontSize: "14px", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.4 : 1 }}
                >‹</button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  style={{ background: "#fff", border: `1px solid ${BRD2}`, borderRadius: "4px", padding: "2px 8px", fontSize: "14px", cursor: page === totalPages ? "not-allowed" : "pointer", opacity: page === totalPages ? 0.4 : 1 }}
                >›</button>
              </div>
              <button
                onClick={onClose}
                style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12px", fontWeight: 500, padding: "0 14px", height: "30px", cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          )}
          {allStatements.length === 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 16px", borderTop: `1px solid ${BRD}` }}>
              <button onClick={onClose} style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12px", fontWeight: 500, padding: "0 14px", height: "30px", cursor: "pointer" }}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirm mark as completed */}
      {confirmCompleteId && confirmTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div role="dialog" aria-modal="true" aria-label="Mark as Completed" style={{ background: "#fff", borderRadius: "7px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", width: "400px", maxWidth: "96vw", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: `1px solid ${BRD}` }}>
              <span style={{ fontSize: "14px", fontWeight: 700, color: T1 }}>Mark as Completed</span>
              <button onClick={() => setConfirmCompleteId(null)} style={{ background: "none", border: "none", color: T3, fontSize: "20px", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: "16px 18px", fontSize: "13px", color: T2, lineHeight: 1.6 }}>
              {confirmPending > 0 && (
                <div style={{ fontWeight: 600, color: ORA, marginBottom: "6px" }}>
                  {confirmPending} row{confirmPending !== 1 ? "s" : ""} will remain unreceipted.
                </div>
              )}
              You will no longer be able to resume this upload. Are you sure?
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", padding: "12px 18px", borderTop: `1px solid ${BRD}` }}>
              <button onClick={() => setConfirmCompleteId(null)} style={{ background: "#fff", color: T2, border: `1px solid ${BRD2}`, borderRadius: "5px", fontSize: "12.5px", fontWeight: 500, padding: "0 14px", height: "32px", cursor: "pointer" }}>
                Cancel
              </button>
              <button
                onClick={() => handleMarkComplete(confirmCompleteId)}
                disabled={loading.markComplete}
                style={{ background: "#2e7d32", color: "#fff", border: "none", borderRadius: "5px", fontSize: "12.5px", fontWeight: 600, padding: "0 16px", height: "32px", cursor: loading.markComplete ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                {loading.markComplete ? <Spinner size="tiny" /> : "Mark as Completed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionsModal;
